import { useMemo, useState } from "react";
import { Trash2, Plus, Search, Download, ChevronDown, ChevronRight, Users, Printer, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { openSignInSheet } from "./signInSheet";

const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const parseISO = (s) => { if (!s) return null; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d, 12); };
const prettyDate = (s) => { const d = parseISO(s); return d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—"; };
const isKids = (cat) => cat === "Kids Camps";
const money = (n) => `$${(Number(n) || 0).toFixed(0)}`;
const teeSummary = (tshirts) => (tshirts || []).filter((t) => (t.qty || 0) > 0).map((t) => `${t.qty}× ${t.size}`).join(", ");

const statusBadge = (s) => {
  const map = {
    paid: "bg-[#C6FF33] text-[#121212]",
    refunded: "bg-stone-200 text-stone-600",
    pending: "bg-amber-200 text-amber-900",
  };
  return map[s] || "bg-stone-200 text-stone-600";
};

// Build one student row per attendee. Kids camps → one row per child; adult → one row per booking.
function rosterRows(bookings, kids) {
  const rows = [];
  bookings.forEach((b) => {
    const parentName = b.guest_name || b.user_name || "Account holder";
    const email = b.guest_email || b.user_email || "";
    const phone = b.parent_phone || b.user_phone || "";
    const regDate = (b.created_at || "").slice(0, 10);
    const refunded = b.status === "refunded";
    const status = refunded ? "refunded" : (b.source === "courtesy" ? "comp" : (b.status || "paid"));
    const tees = teeSummary(b.tshirts);
    const company = b.company_name || "";
    if (kids && (b.children || []).length > 0) {
      b.children.forEach((ch, ci) => {
        rows.push({
          id: `${b.id}-${ci}`, kids: true, bookingId: b.id, refunded,
          child: ch.name, allergies: ch.allergies || "None noted",
          parentName, phone, email, amount: b.amount, regDate, status, company,
          tees: ci === 0 ? tees : "",
        });
      });
    } else {
      rows.push({
        id: b.id, kids, bookingId: b.id, refunded,
        child: kids ? (parentName) : parentName,
        allergies: kids ? "None noted" : "",
        name: parentName, parentName, phone, email, amount: b.amount, regDate, status, company,
        seats: b.num_children || 1,
        tableFour: !!b.table_for_four,
        guestNames: (b.children || []).map((c) => c.name).filter(Boolean),
        tees,
      });
    }
  });
  return rows;
}

function csvDownload(filename, header, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportClassCSV(group) {
  const kids = isKids(group.category);
  const rows = rosterRows(group.bookings, kids);
  const safe = `${group.title}-${group.date}`.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  if (kids) {
    const header = ["Class", "Date", "Category", "Child Name", "Allergies", "Parent Name", "Parent Phone", "Parent Email", "Company", "T-Shirt(s)", "Amount Paid", "Registered On", "Status"];
    csvDownload(`${safe}.csv`, header, rows.map((r) => [group.title, group.date, group.category, r.child, r.allergies, r.parentName, r.phone, r.email, r.company || "", r.tees || "", r.amount, r.regDate, r.status]));
  } else {
    const header = ["Class", "Date", "Category", "Attendee Name", "Ticket", "Seats", "Guest Names", "Phone", "Email", "Company", "T-Shirt(s)", "Amount Paid", "Registered On", "Status"];
    csvDownload(`${safe}.csv`, header, rows.map((r) => [group.title, group.date, group.category, r.name, r.tableFour ? "Table for Four" : "Individual", r.seats || 1, (r.guestNames || []).join("; "), r.phone, r.email, r.company || "", r.tees || "", r.amount, r.regDate, r.status]));
  }
}

const FILTERS = [
  { k: "upcoming", l: "Upcoming" },
  { k: "past", l: "Past" },
  { k: "kids", l: "Kids Camps" },
  { k: "adult", l: "Adult Classes" },
  { k: "all", l: "All" },
];

export default function AdminRegistrations({ ctx }) {
  const { classBookings, classes, exportRegistrationsCSV, courtesyOpen, setCourtesyOpen, courtesy, setCourtesy, addCourtesy, refundBooking, unrefundBooking, waitlist, notifyWaitlist, openCourtesyFor } = ctx;
  const courtesyClass = classes.find((c) => c.id === courtesy.class_id);
  const courtesyIsCamp = (courtesyClass?.category || "").toLowerCase().includes("camp");
  const attendeeWord = courtesyIsCamp ? "camper" : "guest";
  const [filter, setFilter] = useState("upcoming");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const today = todayISO();

  const waitingByClass = useMemo(() => {
    const m = {};
    (waitlist || []).forEach((w) => {
      if (w.status === "waiting") m[w.class_id] = (m[w.class_id] || 0) + 1;
    });
    return m;
  }, [waitlist]);

  // Build one group per class, driven by the full class list, then attach bookings.
  // Any class you have shows here (even with 0 registrations) so its roster/CSV is always reachable.
  const groups = useMemo(() => {
    const g = {};
    // 1) Seed a group for every real class
    (classes || []).forEach((c) => {
      g[c.id] = {
        id: c.id,
        title: c.title || "(untitled)",
        deleted: false,
        date: c.date || "",
        time: c.time || "",
        category: c.category || "",
        capacity: c.capacity ?? null,
        seats_booked: c.seats_booked ?? 0,
        booked_out: !!c.booked_out,
        private: !!c.private,
        bookings: [],
      };
    });
    // 2) Attach bookings; create an orphan group if the class no longer exists
    (classBookings || []).forEach((b) => {
      const id = b.item_id;
      if (!g[id]) {
        g[id] = {
          id,
          title: b.class_title && b.class_title !== "—" ? b.class_title : "(class deleted)",
          deleted: true,
          date: b.class_date || "",
          time: "",
          category: b.class_category || "",
          capacity: null,
          seats_booked: 0,
          booked_out: false,
          bookings: [],
        };
      }
      g[id].bookings.push(b);
    });
    return Object.values(g).map((grp) => {
      const active = grp.bookings.filter((b) => b.status !== "refunded");
      const registered = active.reduce((s, b) => s + (b.num_children || 1), 0);
      const revenue = active.reduce((s, b) => s + (Number(b.amount) || 0), 0);
      const refundedCount = grp.bookings.length - active.length;
      return { ...grp, registered, revenue, refundedCount };
    });
  }, [classBookings, classes]);

  const q = query.trim().toLowerCase();
  const matchesQuery = (grp) => {
    if (!q) return true;
    if ((grp.title || "").toLowerCase().includes(q)) return true;
    return grp.bookings.some((b) => {
      const hay = [
        b.guest_name, b.user_name, b.guest_email, b.user_email, b.parent_phone, b.user_phone,
        ...(b.children || []).map((c) => c.name),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  };

  const filtered = useMemo(() => {
    let list = groups.filter(matchesQuery);
    // When searching, look across ALL classes (any date/category) so a past camp is still findable
    if (!q) {
      if (filter === "upcoming") list = list.filter((g) => g.date && g.date >= today);
      else if (filter === "past") list = list.filter((g) => g.date && g.date < today);
      else if (filter === "kids") list = list.filter((g) => isKids(g.category));
      else if (filter === "adult") list = list.filter((g) => g.category === "Adult Classes");
    }
    // Sort: empty/undated groups last; upcoming soonest-first; past most-recent-first
    list.sort((a, b) => {
      if (!a.date && b.date) return 1;
      if (a.date && !b.date) return -1;
      if (!a.date && !b.date) return 0;
      return filter === "past" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, filter, q, today]);

  const totalRegistered = filtered.reduce((s, g) => s + g.registered, 0);
  const totalRevenue = filtered.reduce((s, g) => s + g.revenue, 0);

  return (
    <div className="mt-10 space-y-4" data-testid="admin-registrations">
      {/* Courtesy form (unchanged) */}
      <div className="border-2 border-[#121212] rounded-xl overflow-hidden">
        <button
          data-testid="toggle-courtesy"
          onClick={() => setCourtesyOpen((o) => !o)}
          className="w-full px-4 py-3 bg-[#C6FF33] text-[#121212] text-xs tracking-[0.2em] uppercase font-bold text-left"
        >
          {courtesyOpen ? "− " : "+ "}Add courtesy registration (free comp)
        </button>
        {courtesyOpen && (
          <div className="p-4 bg-white space-y-3" data-testid="courtesy-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Class / Camp</label>
              <select
                data-testid="courtesy-class"
                value={courtesy.class_id}
                onChange={(e) => setCourtesy({ ...courtesy, class_id: e.target.value })}
                className="w-full px-3 py-2 border-2 border-[#121212] bg-white text-[#121212] focus:outline-none"
              >
                <option value="">Select a class or camp…</option>
                {classes
                  .filter((c) => !c.date || c.date >= new Date().toLocaleDateString("en-CA"))
                  .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
                  .map((c) => <option key={c.id} value={c.id}>{c.title} — {c.date} ({c.category}){c.private ? " · Private" : ""}{c.active === false ? " · Draft" : ""}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input data-testid="courtesy-parent-name" value={courtesy.parent_name} onChange={(e) => setCourtesy({ ...courtesy, parent_name: e.target.value })} placeholder="Registrant name" className="px-3 py-2 border-2 border-[#121212] bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none" />
              <input data-testid="courtesy-parent-email" value={courtesy.parent_email} onChange={(e) => setCourtesy({ ...courtesy, parent_email: e.target.value })} placeholder="Email (optional — sends confirmation)" className="px-3 py-2 border-2 border-stone-200 bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none" />
              <input data-testid="courtesy-parent-phone" value={courtesy.parent_phone} onChange={(e) => setCourtesy({ ...courtesy, parent_phone: e.target.value })} placeholder="Cell phone (optional)" className="px-3 py-2 border-2 border-stone-200 bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-2">{courtesyIsCamp ? "Campers (one row per child)" : "Attendees (one row per guest)"}</label>
              <div className="space-y-2">
                {courtesy.children.map((ch, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input data-testid={`courtesy-child-name-${i}`} value={ch.name} onChange={(e) => setCourtesy({ ...courtesy, children: courtesy.children.map((c, j) => j === i ? { ...c, name: e.target.value } : c) })} placeholder={courtesyIsCamp ? "Child name" : "Attendee name"} className="flex-1 px-3 py-2 border-2 border-[#121212] bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none" />
                    <input data-testid={`courtesy-child-allergies-${i}`} value={ch.allergies} onChange={(e) => setCourtesy({ ...courtesy, children: courtesy.children.map((c, j) => j === i ? { ...c, allergies: e.target.value } : c) })} placeholder="Allergies / dietary (optional)" className="flex-1 px-3 py-2 border-2 border-stone-200 bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none text-sm" />
                    {courtesy.children.length > 1 && (
                      <button onClick={() => setCourtesy({ ...courtesy, children: courtesy.children.filter((_, j) => j !== i) })} className="p-2 text-stone-400 hover:text-red-500"><Trash2 size={16} /></button>
                    )}
                  </div>
                ))}
              </div>
              <button data-testid="courtesy-add-child" onClick={() => setCourtesy({ ...courtesy, children: [...courtesy.children, { name: "", allergies: "" }] })} className="mt-2 inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-bold text-[#121212]"><Plus size={14} /> Add {attendeeWord}</button>
            </div>
            <button data-testid="courtesy-submit" onClick={addCourtesy} className="px-5 py-2.5 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.2em] uppercase font-bold">Add registration (free)</button>
          </div>
        )}
      </div>

      {/* Toolbar: search + export all */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            data-testid="registrations-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search class, attendee, parent, email or phone…"
            className="w-full pl-9 pr-3 py-2.5 border-2 border-stone-200 rounded-lg bg-white text-[#121212] placeholder:text-stone-400 focus:outline-none focus:border-[#121212]"
          />
        </div>
        <button
          data-testid="export-all-registrations-csv"
          onClick={exportRegistrationsCSV}
          disabled={(classBookings || []).length === 0}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-40 whitespace-nowrap rounded-lg"
        >
          <Download size={14} /> Export All
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.k}
            data-testid={`registrations-filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`px-4 py-2 text-xs tracking-[0.15em] uppercase font-bold rounded-full border transition-colors ${
              filter === f.k ? "bg-[#121212] text-white border-[#121212]" : "bg-white text-stone-600 border-stone-300 hover:border-[#121212]"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* Summary line */}
      <p className="text-xs text-stone-500">
        {filtered.length} {filtered.length === 1 ? "class" : "classes"} · {totalRegistered} registered · {money(totalRevenue)} revenue
      </p>

      {/* Class cards */}
      {filtered.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-stone-500 text-sm">No registrations match this view.</p>
          {filter === "upcoming" && !q && (
            <p className="text-stone-400 text-xs mt-1">Looking for a camp that already happened? Check <b>Past</b>, <b>Kids Camps</b>, or <b>All</b> — or search its name above.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((g) => {
            const kids = isKids(g.category);
            const rows = rosterRows(g.bookings, kids);
            const open = openId === g.id;
            const noRecords = g.registered === 0 && (g.seats_booked || 0) > 0;
            const capLabel = g.capacity != null
              ? `${noRecords ? g.seats_booked : g.registered} / ${g.capacity}`
              : `${g.registered}`;
            return (
              <div key={g.id} data-testid={`class-group-${g.id}`} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded ${kids ? "bg-[#E0218A] text-white" : "bg-[#C6FF33] text-[#121212]"}`}>{g.category || "Class"}</span>
                        {g.date && g.date < today && <span className="px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded bg-stone-200 text-stone-600">Past</span>}
                        {g.refundedCount > 0 && <span className="px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded bg-orange-100 text-orange-700">{g.refundedCount} refunded</span>}
                        {g.private && <span data-testid={`reg-private-badge-${g.id}`} className="px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded bg-[#121212] text-[#C6FF33]">Private</span>}
                        {g.booked_out && <span className="px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded bg-[#121212] text-white">Marked full</span>}
                        {waitingByClass[g.id] > 0 && <span data-testid={`waitlist-badge-${g.id}`} className="px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded bg-[#2D6BFF] text-white">Waitlist {waitingByClass[g.id]}</span>}
                      </div>
                      <h3 className="font-serif font-black text-xl text-[#121212] mt-2 leading-tight truncate">{g.title}</h3>
                      <p className="text-sm text-stone-500 mt-0.5">{prettyDate(g.date)}{g.time ? ` · ${g.time}` : ""}</p>
                      {noRecords && <p className="text-xs text-amber-600 mt-1">No online registrations recorded for this class — nothing to export yet.</p>}
                    </div>
                    <div className="flex gap-5 sm:gap-8">
                      <div className="text-center">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-stone-400">Registered</p>
                        <p className="font-serif font-black text-2xl text-[#121212] inline-flex items-center gap-1"><Users size={16} className="text-stone-400" />{capLabel}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-stone-400">Revenue</p>
                        <p className="font-serif font-black text-2xl text-[#121212]">{money(g.revenue)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button
                      data-testid={`view-roster-${g.id}`}
                      onClick={() => setOpenId(open ? null : g.id)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 border-2 border-[#121212] text-[#121212] hover:bg-[#121212] hover:text-white text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"
                    >
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} View Roster
                    </button>
                    <button
                      data-testid={`export-class-csv-${g.id}`}
                      onClick={() => exportClassCSV(g)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-stone-100 hover:bg-stone-200 text-[#121212] text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"
                    >
                      <Download size={14} /> Export CSV
                    </button>
                    {classes.some((c) => c.id === g.id) && !(g.date && g.date < new Date().toLocaleDateString("en-CA")) && (
                      <button
                        data-testid={`courtesy-group-btn-${g.id}`}
                        onClick={() => openCourtesyFor(g)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#C6FF33] hover:bg-[#b3ec1f] text-[#121212] text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"
                      >
                        <UserPlus size={14} /> Add courtesy guest
                      </button>
                    )}
                    {kids && (
                      <button
                        data-testid={`print-signin-${g.id}`}
                        onClick={() => { if (!openSignInSheet(g, rows.filter((r) => !r.refunded))) toast.error("Please allow pop-ups to print the sign-in sheet."); }}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#E0218A] hover:bg-[#c41b78] text-white text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"
                      >
                        <Printer size={14} /> Print Sign-in Sheet
                      </button>
                    )}
                    {waitingByClass[g.id] > 0 && (
                      <button
                        data-testid={`notify-waitlist-${g.id}`}
                        onClick={() => notifyWaitlist(g.id)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#2D6BFF] hover:bg-[#1f52cc] text-white text-xs tracking-[0.15em] uppercase font-bold rounded-lg transition-colors"
                      >
                        <Users size={14} /> Notify Waitlist ({waitingByClass[g.id]})
                      </button>
                    )}
                  </div>
                </div>

                {open && (
                  <div data-testid={`roster-${g.id}`} className="border-t border-stone-200 bg-stone-50/60 p-4 sm:p-5">
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-stone-400 border-b border-stone-200">
                            {kids ? (
                              <>
                                <th className="py-2 pr-3">Child</th>
                                <th className="py-2 pr-3">Allergies</th>
                                <th className="py-2 pr-3">Parent</th>
                                <th className="py-2 pr-3">Phone</th>
                                <th className="py-2 pr-3">Email</th>
                              </>
                            ) : (
                              <>
                                <th className="py-2 pr-3">Attendee</th>
                                <th className="py-2 pr-3">Phone</th>
                                <th className="py-2 pr-3">Email</th>
                              </>
                            )}
                            <th className="py-2 pr-3">T-Shirt</th>
                            <th className="py-2 pr-3">Paid</th>
                            <th className="py-2 pr-3">Registered</th>
                            <th className="py-2 pr-3">Status</th>
                            <th className="py-2 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.id} className={`border-b border-stone-100 last:border-0 ${r.refunded ? "opacity-50" : ""}`}>
                              {kids ? (
                                <>
                                  <td className={`py-2 pr-3 font-medium text-stone-900 ${r.refunded ? "line-through" : ""}`}>{r.child}</td>
                                  <td className="py-2 pr-3 text-stone-600">{r.allergies}</td>
                                  <td className="py-2 pr-3 text-stone-700">{r.parentName}{r.company && <span className="block text-xs text-stone-500">{r.company}</span>}</td>
                                  <td className="py-2 pr-3 text-stone-700">{r.phone || "—"}</td>
                                  <td className="py-2 pr-3 text-stone-700">{r.email || "—"}</td>
                                </>
                              ) : (
                                <>
                                  <td className={`py-2 pr-3 font-medium text-stone-900 ${r.refunded ? "line-through" : ""}`}>
                                    {r.name}{r.tableFour ? " · Table for Four (4 seats)" : (r.seats > 1 ? ` (${r.seats} seats)` : "")}
                                    {r.tableFour && r.guestNames.length > 0 && <span className="block text-xs font-normal text-stone-500">Guests: {r.guestNames.join(", ")}</span>}
                                    {r.company && <span data-testid={`roster-company-${r.id}`} className="block text-xs font-normal text-stone-500">{r.company}</span>}
                                  </td>
                                  <td className="py-2 pr-3 text-stone-700">{r.phone || "—"}</td>
                                  <td className="py-2 pr-3 text-stone-700">{r.email || "—"}</td>
                                </>
                              )}
                              <td className="py-2 pr-3 text-stone-600" data-testid={`roster-tee-${r.id}`}>{r.tees || "—"}</td>
                              <td className="py-2 pr-3 text-stone-700">{money(r.amount)}</td>
                              <td className="py-2 pr-3 text-stone-500">{r.regDate || "—"}</td>
                              <td className="py-2 pr-3"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${statusBadge(r.status)}`}>{r.status}</span></td>
                              <td className="py-2 text-right">
                                {r.refunded ? (
                                  <button data-testid={`unrefund-${r.bookingId}`} onClick={() => unrefundBooking(r.bookingId)} className="text-[11px] tracking-wide uppercase font-bold text-stone-500 hover:text-[#121212]">Undo</button>
                                ) : (
                                  <button data-testid={`refund-${r.bookingId}`} onClick={() => refundBooking(r.bookingId)} className="text-[11px] tracking-wide uppercase font-bold text-orange-600 hover:text-orange-800">Refund</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-2">
                      {rows.map((r) => (
                        <div key={r.id} className={`bg-white border border-stone-200 rounded-lg p-3 ${r.refunded ? "opacity-60" : ""}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-medium text-stone-900 ${r.refunded ? "line-through" : ""}`}>{kids ? r.child : `${r.name}${r.tableFour ? " · Table for Four (4)" : (r.seats > 1 ? ` (${r.seats} seats)` : "")}`}</span>
                            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${statusBadge(r.status)}`}>{r.status}</span>
                          </div>
                          {!kids && r.tableFour && r.guestNames.length > 0 && <p className="text-xs text-stone-500 mt-1">Guests: {r.guestNames.join(", ")}</p>}
                          {kids && <p className="text-xs text-stone-500 mt-1">Allergies: {r.allergies}</p>}
                          {r.company && <p className="text-xs text-stone-500 mt-1">Company: {r.company}</p>}
                          {r.tees && <p className="text-xs text-stone-600 mt-0.5">T-Shirt: {r.tees}</p>}
                          {kids && <p className="text-xs text-stone-600 mt-0.5">Parent: {r.parentName}</p>}
                          <p className="text-xs text-stone-600 mt-0.5">{r.phone || "No phone"} · {r.email || "No email"}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-stone-500">{money(r.amount)} · {r.regDate || "—"}</p>
                            {r.refunded ? (
                              <button data-testid={`unrefund-m-${r.bookingId}`} onClick={() => unrefundBooking(r.bookingId)} className="text-[11px] uppercase font-bold text-stone-500">Undo</button>
                            ) : (
                              <button data-testid={`refund-m-${r.bookingId}`} onClick={() => refundBooking(r.bookingId)} className="text-[11px] uppercase font-bold text-orange-600">Refund</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
